/**
 * Tesorería sobre Supabase (ex Firestore). Las operaciones que tocan balances
 * (crear/editar/borrar transacciones, reset de período) van por RPCs atómicos
 * (migración 0004): un fallo revierte transacción y balances juntos, cosa que
 * los writeBatch originales no garantizaban tras el fire-and-forget.
 */
import {
  subscribeUser,
  listUserRows,
  listRowsArrayContains,
  insertRow,
  updateRow,
  callRpc,
  handleDbError,
} from '../lib/db';
import { Account, AccountType, Transaction, TransactionType, TransactionDetail } from '../types';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export const subscribeToAccounts = (userId: string, callback: (accounts: Account[]) => void) => {
  return subscribeUser<Account>(
    'accounts',
    userId,
    callback,
    { orderBy: 'name', ascending: true },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/accounts`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const subscribeToTransactions = (userId: string, callback: (transactions: Transaction[]) => void) => {
  return subscribeUser<Transaction>(
    'transactions',
    userId,
    callback,
    { orderBy: 'date', ascending: false, limit: 500 },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/transactions`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

// Sin límite: la Planificación concilia y muestra el real de cualquier mes, no solo de las
// últimas 500 (el límite de la lista principal). subscribeUser pagina de a 1000.
export const subscribeToAllTransactions = (userId: string, callback: (transactions: Transaction[]) => void) => {
  return subscribeUser<Transaction>(
    'transactions',
    userId,
    callback,
    { orderBy: 'date', ascending: false },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/transactions`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const createTransaction = async (
  userId: string,
  data: {
    description: string;
    type: TransactionType;
    date: string;
    details: TransactionDetail[];
    pendingInvoice?: boolean;
  },
): Promise<string | undefined> => {
  try {
    return await callRpc<string>('create_transaction', {
      p_user_id: userId,
      p_description: data.description,
      p_type: data.type,
      p_date: data.date,
      p_details: data.details,
      p_pending_invoice: data.pendingInvoice ?? null,
    });
  } catch (error) {
    handleDbError(error, OperationType.WRITE, `users/${userId}/transactions`);
  }
};

/**
 * Compat con los callers que armaban batches: crea la transacción (atómica con
 * sus balances) y devuelve el id. Ya no existe el batch diferido de Firestore.
 */
export const addTransactionToBatch = async (
  userId: string,
  _batch: unknown,
  data: {
    description: string;
    type: TransactionType;
    date: string;
    details: TransactionDetail[];
    pendingInvoice?: boolean;
  },
): Promise<string | undefined> => {
  return createTransaction(userId, data);
};

export const linkTransactionToJournalEntry = async (
  userId: string,
  transactionId: string,
  journalEntryId: string,
) => {
  try {
    await updateRow('transactions', transactionId, { journalEntryId });
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/transactions/${transactionId}`);
  }
};

export const deleteTransaction = async (userId: string, transaction: Transaction) => {
  try {
    await callRpc('delete_transaction', {
      p_user_id: userId,
      p_transaction_id: transaction.id,
    });
  } catch (error) {
    handleDbError(error, OperationType.DELETE, `users/${userId}/transactions/${transaction.id}`);
  }
};

export const updateTransaction = async (
  userId: string,
  oldTransaction: Transaction,
  newData: {
    description: string;
    type: TransactionType;
    date: string;
    details: TransactionDetail[];
  },
) => {
  try {
    await callRpc('update_transaction', {
      p_user_id: userId,
      p_transaction_id: oldTransaction.id,
      p_description: newData.description,
      p_type: newData.type,
      p_date: newData.date,
      p_details: newData.details,
    });
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/transactions/${oldTransaction.id}`);
  }
};

export const checkTransactionConnections = async (userId: string, transactionId: string) => {
  try {
    const [planningRows, managementRows] = await Promise.all([
      listRowsArrayContains<{ id: string }>('plannedMatches', userId, 'transactionIds', transactionId),
      listRowsArrayContains<{ id: string; type: string; number: string }>(
        'managedDocuments',
        userId,
        'transactionIds',
        transactionId,
      ),
    ]);

    const connections: { type: 'PLANNING' | 'MANAGEMENT'; id: string; name?: string }[] = [];
    planningRows.forEach((row) => connections.push({ type: 'PLANNING', id: row.id }));
    managementRows.forEach((row) =>
      connections.push({ type: 'MANAGEMENT', id: row.id, name: `${row.type} ${row.number}` }),
    );
    return connections;
  } catch (error) {
    try {
      handleDbError(error, OperationType.GET, `users/${userId}/connections/${transactionId}`);
    } catch {
      /* logueado */
    }
    return [];
  }
};

export const createTransfer = async (
  userId: string,
  data: {
    description: string;
    date: string;
    fromAccountId: string;
    toAccountId: string;
    amount: number;
  },
) => {
  const details: TransactionDetail[] = [
    { accountId: data.fromAccountId, amount: data.amount, type: 'DEBIT' },
    { accountId: data.toAccountId, amount: data.amount, type: 'CREDIT' },
  ];

  return createTransaction(userId, {
    description: data.description,
    type: 'TRANSFER',
    date: data.date,
    details,
  });
};

export const createAccount = async (
  userId: string,
  data: {
    name: string;
    type: AccountType;
    initialBalance: number;
    institutionId?: string;
    creditLimit?: number;
    overdraftType?: import('../types').OverdraftType;
    overdraftValue?: number;
  },
) => {
  try {
    const account = await insertRow<Account>('accounts', {
      ...data,
      balance: data.initialBalance,
      userId,
      createdAt: new Date().toISOString(),
    });
    return account;
  } catch (error) {
    handleDbError(error, OperationType.WRITE, `users/${userId}/accounts`);
  }
};

export const updateAccount = async (userId: string, accountId: string, data: Partial<Account>) => {
  try {
    await updateRow('accounts', accountId, data);
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/accounts/${accountId}`);
  }
};

export const recalculateAccountBalance = async (userId: string, accountId: string) => {
  try {
    const accounts = await listUserRows<Account>('accounts', userId);
    const account = accounts.find((a) => a.id === accountId);
    if (!account) throw new Error('Account not found');

    const initialBalance = account.initialBalance || 0;
    const transactions = await listUserRows<Transaction>('transactions', userId);

    let calculatedBalance = initialBalance;
    transactions.forEach((tx) => {
      tx.details.forEach((detail) => {
        if (detail.accountId === accountId) {
          if (detail.type === 'CREDIT') {
            calculatedBalance += detail.amount;
          } else {
            calculatedBalance -= detail.amount;
          }
        }
      });
    });

    await updateRow('accounts', accountId, { balance: calculatedBalance });
    return calculatedBalance;
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/accounts/${accountId}`);
  }
};

export const resetPeriod = async (userId: string) => {
  try {
    await callRpc('reset_period', { p_user_id: userId });
  } catch (error) {
    handleDbError(error, OperationType.WRITE, `users/${userId}/resetPeriod`);
  }
};

export const togglePendingInvoice = async (userId: string, transactionId: string, pendingInvoice: boolean) => {
  try {
    await updateRow('transactions', transactionId, {
      pendingInvoice,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    handleDbError(error, OperationType.UPDATE, `users/${userId}/transactions`);
  }
};
